<template>
  <ButtonInput
    id="shareButton"
    data-gtm-id="share-button"
    :icon="shouldShowDefaultIcon ? (icon ? icon : 'pi pi-copy') : undefined"
    :text="text"
    :link="link"
    :pt="pt"
    @click="copyToClipboard"
    :loading="copying"
    :style="{
      ...(backgroundColor && { backgroundColor }),
      ...(textColor && { color: textColor }),
      ...(borderColor && { borderColor }),
    }"
    v-bind="$attrs"
  >
    <!-- Custom content with flexible icon positioning -->
    <div class="flex items-center gap-2">
      <!-- Left icon -->
      <span v-if="slots.iconLeft">
        <slot name="iconLeft" />
      </span>
      
      <!-- Default/center icon (for backward compatibility) -->
      <span v-else-if="slots.icon">
        <slot name="icon" />
      </span>
      
      <!-- Button label -->
      <span id="shareButtonLabel">{{ buttonLabel ? buttonLabel : t('buttons.copyLink') }}</span>
      
      <!-- Right icon -->
      <span v-if="slots.iconRight">
        <slot name="iconRight" />
      </span>
    </div>
  </ButtonInput>
</template>

<script setup lang="ts">
import { ref, computed, useSlots } from 'vue';
import { errorToast, successToast } from '@/services/ToastService';
import { inject } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const t: any = inject('t');
const slots = useSlots();

const copying = ref(false);

interface Props {
  icon?: string; // Optional icon for the button
  contentToCopy?: string; // Optional prop to allow passing specific content to copy
  buttonLabel?: string; // Optional prop to customize button label
  openEmail?: boolean; // Optional prop to open email client with the content
  emailSubject?: string; // Optional email subject line
  backgroundColor?: string; // Optional background color for the button
  textColor?: string; // Optional text color for the button
  borderColor?: string; // Optional border color for the button
  text?: boolean; // Optional text variant for PrimeVue Button
  link?: boolean; // Optional link variant for the button that makes it look like a hyperlink
  pt?: Record<string, any>; // Optional passthrough object for PrimeVue Button styling
}

const props = defineProps<Props>();

// Check if we should show the default icon (when no custom slots are provided)
const shouldShowDefaultIcon = computed(() => {
  // Don't show default icon if any of the custom icon slots are being used
  return !slots.icon && !slots.iconLeft && !slots.iconRight;
});

const copyToClipboard = async () => {
  // Prevent multiple simultaneous operations
  if (copying.value) {
    return;
  }

  copying.value = true;

  try {
    let content = props.contentToCopy;

    if (!content) {
      content = window.location.href; // Fallback to current page URL
    }

    await navigator.clipboard.writeText(content);

    // Open email client if requested
    if (props.openEmail) {
      const subject = props.emailSubject || 'Shared Content';
      const emailBody = encodeURIComponent(content);
      const emailSubject = encodeURIComponent(subject);
      const mailtoUrl = `mailto:?subject=${emailSubject}&body=${emailBody}`;
      window.open(mailtoUrl, '_blank');
    }

    successToast(props.openEmail ? t('toasts.copyAndEmailSuccess') : t('toasts.copySuccess'));
  } catch (error) {
    console.error('Failed to copy URL:', error);

    errorToast(t('toasts.copyError'));
  } finally {
    copying.value = false;
  }
};
</script>
